import React, { useEffect, useRef, useState } from "react";
import Axios from "axios";
import { connect } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { ToastsContainer, ToastsStore } from "react-toasts";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min";

import Config from "./../../Config";
import {
  setAddress,
  set_OfficeAddress,
} from "../../../store/Actions/authActions";
import ReactCodeInput from "react-code-input";

import OnboardingLayout, { Icon } from "../OnboardingLayout";
import { WoshModal, ResultScreen } from "../Feedback";

const MIN_QUERY = 3;

/**
 * Onboarding 02b/02c — rejoindre un point de collecte. Un seul écran :
 * la recherche, puis la liste des résultats. On sélectionne un point,
 * et « Rejoindre ce point » ouvre la confirmation — avec code si le
 * point est sécurisé.
 */
const ConnectPoint = (props) => {
  const { t } = props;

  const [uid, setUid] = useState(null);
  const [client, setClient] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const [search, setSearch] = useState("");
  const [points, setPoints] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [codeSecure, setCodeSecure] = useState("");
  const [joinFailed, setJoinFailed] = useState(null);

  const codeRef = useRef();

  useEffect(() => {
    localStorage.setItem("path", JSON.stringify(window.location.pathname));
    setUid(props?.authUser?.user_uid);
    setClient(props?.authUser?.user_client);
    setAccessToken(props?.authUser?.user_access_token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** `myLove` marque le passage par l'inscription : il pilote la jauge d'étapes. */
  const onboarding = props.location?.state?.myLove === "Pakistan";
  const selected = points?.find((item) => item.id === selectedId) || null;

  const handleSearch = async () => {
    setSearching(true);
    setSelectedId(null);

    await Axios({
      url: `${Config.apiURL}/collection_points`,
      method: "GET",
      params: { q: search },
      headers: {
        uid: `${uid}`,
        client: `${client}`,
        "access-token": `${accessToken}`,
        "content-type": "application/json",
      },
    })
      .then((response) => {
        setPoints(
          response?.data?.collection_points?.filter(
            (point) => point?.companies?.length > 0
          ) || []
        );
        setSearching(false);
      })
      .catch(() => {
        setSearching(false);
        setPoints([]);
        ToastsStore.error(t("Something went wrong"));
      });
  };

  const handleJoin = async () => {
    if (!selected) {
      return;
    }

    setLoading(true);

    const data = {
      collection_point: {
        collection_point_id: selected.id,
        ...(selected.secure ? { code: codeSecure } : {}),
      },
      company_id: selected.companies?.[0]?.id,
    };

    await Axios({
      url: `${Config.apiURL}/customer/collection_points`,
      method: "POST",
      data,
      headers: {
        uid: `${uid}`,
        client: `${client}`,
        "access-token": `${accessToken}`,
        "content-type": "application/json",
      },
    })
      .then(() => {
        setLoading(false);
        setCodeSecure("");
        setShowModal(false);
        setErr("");
        setJoinFailed(null);
        ToastsStore.success(t("Collection point has been added"));
        props?.dispatch(set_OfficeAddress());
        props?.dispatch(setAddress());
        props.history.push("/thank-you", {
          myLove: props.location?.state?.myLove,
        });
      })
      .catch((error) => {
        setShowModal(false);
        setCodeSecure("");
        setLoading(false);
        setJoinFailed(
          selected.secure
            ? t("Your code is incorrect! Please try again.")
            : error?.response?.data?.message ||
                t("This point is reserved for members of the organisation.")
        );
      });
  };

  const renderResults = () => {
    if (points === null) {
      return (
        <div className="wosh-ob__empty">
          <div className="wosh-ob__empty-badge">
            <Icon name="business" />
          </div>
          <p className="wosh-ob__empty-text">
            {t("Type at least 3 letters to find your collection point.")}
          </p>
        </div>
      );
    }

    if (points.length === 0) {
      return (
        <div className="wosh-ob__empty">
          <div className="wosh-ob__empty-badge">
            <Icon name="search" />
          </div>
          <p className="wosh-ob__empty-text">
            {t("No collection point found")}
          </p>
        </div>
      );
    }

    return (
      <div className="wosh-ob__results">
        <div className="wosh-ob__count">
          {points.length}{" "}
          {points.length === 1 ? t("result found") : t("results found")}
        </div>

        <ul className="wosh-ob__list" role="radiogroup">
          {points.map((point) => {
            const on = point.id === selectedId;
            /*
             * Un point héberge une ou plusieurs entreprises. La première
             * nomme le point ; les suivantes sont listées dessous, sinon
             * la ligne répéterait le titre.
             */
            const companies = (point.companies || [])
              .map((company) => company.company_name)
              .filter(Boolean);
            const others = companies.slice(1);

            return (
              <li key={point.id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`wosh-ob__point${on ? " wosh-ob__point--on" : ""}`}
                  onClick={() => setSelectedId(on ? null : point.id)}
                >
                  <span className="wosh-ob__point-badge">
                    <Icon name="business" />
                  </span>

                  <span className="wosh-ob__point-body">
                    <span className="wosh-ob__point-name">
                      {companies[0]}
                    </span>
                    <span className="wosh-ob__point-address">
                      {point.address}
                    </span>
                    {others.length > 0 && (
                      <span className="wosh-ob__point-meta">
                        <Icon name="business" />
                        {others.join(" · ")}
                      </span>
                    )}
                  </span>

                  <span className="wosh-ob__tick">
                    <Icon name="check" />
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="wosh-ob__cta-wrap">
          <button
            type="button"
            className="wosh-ob__cta"
            disabled={!selected || loading}
            onClick={() => {
              setErr("");
              setShowModal(true);
            }}
          >
            {t("Join this point")}
          </button>
        </div>
      </div>
    );
  };

  if (joinFailed) {
    return (
      <ResultScreen
        tone="failure"
        title={t("Connection failed")}
        lede={t(
          "We could not attach you to this collection point. Check your selection and try again."
        )}
        reason={joinFailed}
        actions={[
          {
            label: t("contact us"),
            onClick: () =>
              window.open("https://www.wosh.be/contact", "_blank", "noreferrer"),
          },
          {
            label: t("Try again"),
            primary: true,
            onClick: () => setJoinFailed(null),
          },
        ]}
      />
    );
  }

  return (
    <OnboardingLayout
      onBack={() =>
        props.history.push("/use-wosh", {
          myLove: props.location?.state?.myLove,
        })
      }
      steps={onboarding ? { current: 4, total: 5 } : null}
      top
    >
      <ToastsContainer store={ToastsStore} />

      <WoshModal
        open={showModal && Boolean(selected)}
        onClose={() => {
          setShowModal(false);
          setCodeSecure("");
        }}
        icon={<Icon name="business" />}
        title={`${t("Join")} ${selected?.companies?.[0]?.company_name || ""} ?`}
        lede={t("Your pickups and deliveries will happen at this point.")}
        info={
          selected?.address && (
            <>
              <Icon name="location" />
              {selected.address}
            </>
          )
        }
        error={err}
        note={t("You can change your point later in your profile.")}
        cancelLabel={t("Cancel")}
        confirmLabel={loading ? t("processing") : t("Confirm")}
        confirmDisabled={loading || (selected?.secure && codeSecure.length < 4)}
        onConfirm={handleJoin}
      >
        {selected?.secure && (
          <div className="wosh-modal__code">
            <ReactCodeInput
              ref={codeRef}
              type="number"
              fields={4}
              value={codeSecure}
              onChange={(value) => setCodeSecure(value)}
              inputStyle={{
                width: 54,
                height: 60,
                margin: "0 6px",
                borderRadius: 16,
                border: "1px solid #D8DDF0",
                background: "#F8F9FA",
                fontFamily: "Manrope, system-ui, sans-serif",
                fontSize: 22,
                fontWeight: 800,
                textAlign: "center",
                color: "#1B1F3A",
              }}
            />
          </div>
        )}
      </WoshModal>

      <div className="wosh-ob__eyebrow">{t("Collection point")}</div>
      <h1 className="wosh-ob__title wosh-ob__centered">
        {t("Join your collection point")}
      </h1>
      <p className="wosh-ob__lede wosh-ob__centered">
        {t("Company, office, coworking space or residence.")}
      </p>

      <div
        className={`wosh-ob__search${
          search.length >= MIN_QUERY ? " wosh-ob__search--filled" : ""
        }`}
      >
        <Icon name="search" />
        <input
          type="text"
          name="search"
          value={search}
          placeholder={t("company, building or residence name")}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && search.length >= MIN_QUERY) {
              handleSearch();
            }
          }}
        />
        <button
          type="button"
          className="wosh-ob__search-btn"
          disabled={searching || search.length < MIN_QUERY}
          onClick={handleSearch}
        >
          {searching ? t("processing") : t("search")}
        </button>
      </div>

      {err && (
        <p className="wosh-ob__error" style={{ marginTop: 20 }}>
          {err}
        </p>
      )}

      {renderResults()}

      <div className="wosh-ob__help">
        <Icon name="help" />
        <span>{t("unable to connect?")}</span>
        <a
          href="https://www.wosh.be/contact"
          target="_blank"
          rel="noreferrer noopener"
        >
          {t("contact us")}
        </a>
      </div>
    </OnboardingLayout>
  );
};

const mapStateToProps = (state) => ({
  authUser: state?.auth?.user,
});

export default compose(
  withTranslation(),
  connect(mapStateToProps)
)(ConnectPoint);
